import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useUpdateSchoolClass } from '@hooks/mutations/useUpdateSchoolClass'
import {
  HEADING_TYPE,
  TEXT_SIZES,
  DIVIDER_SIZE,
  SCHOOL_RESOURCE_TYPES,
  BUTTON_TYPE,
  BUTTON_SIZE,
} from '@helpers/constants'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Divider from '@components/Divider'
import GridColumns from '@components/GridColumns'
import StudentBookCard from '@components/StudentBookCard'
import { useResponsive } from '@hooks/useResponsive'
import { useUpsertSchoolGroup } from '@hooks/mutations/useUpsertSchoolGroup'
import { useUpdateDemoSchoolClass } from '@hooks/mutations/useUpdateDemoSchoolClass'
import { useUpdateDemoSchoolGroup } from '@hooks/mutations/useUpdateDemoSchoolGroup'
import { useExamsAllowed } from '@hooks/useExamsAllowed'
import StudentBookCardNew from '@components/StudentBookCardNew'
import { AppContext } from '@state/context'
import { buildStudentLabel } from '@helpers/students'
import Button from '@components/Button'
import { useUpdateBookArchiveStatus } from '@hooks/mutations/useUpdateBookArchiveStatus'
import SelectButton from '@components/SelectButton'
import { evaluateBookProgress } from '@helpers/exams'

const SharedBooksList = (props) => {
  const { items = [], resourceType, resource } = props

  const [showArchived, setShowArchived] = useState(false)
  const [bookSortOrder, setBookSortOrder] = useState('default')

  const sortedItems = items.sort((a, b) => {
    const bookAProgress = evaluateBookProgress(a.sections, resource?.students)
    const bookBProgress = evaluateBookProgress(b.sections, resource?.students)

    // Sort by passed
    if (bookSortOrder === 'allPassedCount') {
      return bookBProgress.allPassedCount - bookAProgress.allPassedCount
    }

    // Sort by failed
    if (bookSortOrder === 'anyFailedCount') {
      return bookBProgress.anyFailedCount - bookAProgress.anyFailedCount
    }

    // Sort by in progress
    if (bookSortOrder === 'inProgressCount') {
      return bookBProgress.inProgressCount - bookAProgress.inProgressCount
    }

    // Default, sort by date, latest shared first
    return new Date(b.pivot.created_at) - new Date(a.pivot.created_at)
  })

  const currentBooks = sortedItems.filter(
    (item) => item.pivot?.is_archived !== true
  )
  const archivedBooks = sortedItems.filter(
    (item) => item.pivot?.is_archived === true
  )

  const [state, dispatch] = useContext(AppContext)

  const {
    app: { selectedStudent },
  } = state

  const updateHook = (() => {
    if (resourceType === SCHOOL_RESOURCE_TYPES.CLASS)
      return useUpdateSchoolClass
    if (resourceType === SCHOOL_RESOURCE_TYPES.GROUP)
      return useUpsertSchoolGroup
    if (resourceType === SCHOOL_RESOURCE_TYPES.DEMO_CLASS)
      return useUpdateDemoSchoolClass
    if (resourceType === SCHOOL_RESOURCE_TYPES.DEMO_GROUP)
      return useUpdateDemoSchoolGroup
  })()

  const { post: postUpdate, isPosting: isPostingUpdate } = updateHook()

  const unShareBook = async (bookId) => {
    await postUpdate(
      {
        id: resource.id,
        books: { disconnect: [bookId] },
      },
      'Delning borttagen.',
      'Nåt gick fel när delning skulle tas bort.'
    )
  }

  const {
    post: postUpdateBookArchiveStatus,
    isPosting: isPostingUpdateBookArchiveStatus,
  } = useUpdateBookArchiveStatus()

  const updateBookArchiveStatus = async (pivotId, isArchive) => {
    await postUpdateBookArchiveStatus({
      resourceType: resourceType,
      resourceId: resource.id,
      pivotId: pivotId,
      isArchived: isArchive,
    })
  }

  const { breakpoints } = useResponsive()
  const cols = (() => {
    if (breakpoints.mobile) return 1
    if (breakpoints.tablet) return 2
    if (breakpoints.wide) return 2
    return 3
  })()

  const examsAllowed = useExamsAllowed()

  const booksToShow = !examsAllowed
    ? sortedItems
    : showArchived
    ? archivedBooks
    : currentBooks

  const resourceTypeName =
    resourceType === SCHOOL_RESOURCE_TYPES.GROUP ||
    resourceType === SCHOOL_RESOURCE_TYPES.DEMO_GROUP
      ? 'Gruppen'
      : 'Klassen'

  if (!items?.length) {
    return <Text>{`${resourceTypeName} har inga böcker ännu.`}</Text>
  }

  return (
    <>
      <div className='flex items-center justify-between'>
        <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
          {!selectedStudent ? (
            <>
              {`${resourceTypeName}s böcker`}

              {/* {examsAllowed ? (
                <>{`${resourceTypeName}s ${
                  showArchived ? 'arkiverade' : 'aktuella'
                } böcker`}</>
              ) : (
                <>{`${resourceTypeName}s böcker`}</>
              )} */}
            </>
          ) : (
            <>{buildStudentLabel(selectedStudent, resource?.students)}</>
          )}
        </Heading>

        <div>
          {examsAllowed && (
            <>
              <Button
                type={
                  !showArchived ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY
                }
                size={BUTTON_SIZE.MEDIUM}
                icon={'Star'}
                iconLeft
                margin
                handleClick={() => {
                  setShowArchived(false)
                }}
              >
                Aktuella
              </Button>

              <Button
                type={
                  showArchived ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY
                }
                size={BUTTON_SIZE.MEDIUM}
                icon={'FileCabinet'}
                iconLeft
                margin
                handleClick={() => {
                  setShowArchived(true)
                }}
              >
                Arkiverade
              </Button>
            </>
          )}
        </div>

        <div>
          {examsAllowed && (
            <div className='flex items-center gap-1'>
              <Text size={TEXT_SIZES.XXSMALL}>Sortering</Text>
              <SelectButton
                onChange={(e) => {
                  setBookSortOrder(e.target.value)
                }}
                name={'bookSortOrder'}
                id={'bookSortOrder'}
                options={[
                  { value: 'default', name: 'Datum' },
                  { value: 'allPassedCount', name: 'Godkänt' },
                  { value: 'anyFailedCount', name: 'Icke godkänt' },
                  { value: 'inProgressCount', name: 'Pågående' },
                ]}
                defaultValue={bookSortOrder}
                extraClasses={'!border-solid border border-mediumGrey'}
              />
            </div>
          )}
        </div>
      </div>

      <Divider size={DIVIDER_SIZE.SMALL} />

      {booksToShow && booksToShow.length > 0 ? (
        <GridColumns cols={cols}>
          {booksToShow.map((book, index) => {
            const key = `book-${book.id}-${index}`

            if (examsAllowed) {
              return (
                <StudentBookCardNew
                  key={key}
                  book={book}
                  resourceType={resourceType}
                  resource={resource}
                  isShared
                  unShare={() => {
                    unShareBook(book.id)
                  }}
                  updateBookArchiveStatus={updateBookArchiveStatus}
                />
              )
            } else {
              return (
                <StudentBookCard
                  key={key}
                  book={book}
                  resourceType={resourceType}
                  isShared
                  unShare={() => {
                    unShareBook(book.id)
                  }}
                />
              )
            }
          })}
        </GridColumns>
      ) : (
        <>
          {showArchived ? (
            <Text>{`${resourceTypeName} har inga arkiverade böcker.`}</Text>
          ) : (
            <Text>{`${resourceTypeName} har inga aktuella böcker.`}</Text>
          )}
        </>
      )}
    </>
  )
}

SharedBooksList.propTypes = {
  items: PropTypes.array,
  resourceType: PropTypes.string,
}

export default SharedBooksList
